import { useI18n, type MsgKey } from "../../i18n";

const ACTORS: MsgKey[] = [
  "sysGuideActorRequester",
  "sysGuideActorEngine",
  "sysGuideActorAi",
  "sysGuideActorAdmin",
];

interface Message {
  from: number;
  to: number;
  key: MsgKey;
}

const MESSAGES: Message[] = [
  { from: 0, to: 1, key: "sysGuideSeqSubmit" },
  { from: 1, to: 1, key: "sysGuideSeqFreeze" },
  { from: 1, to: 1, key: "sysGuideSeqDeterministic" },
  { from: 1, to: 2, key: "sysGuideSeqAsk" },
  { from: 2, to: 1, key: "sysGuideSeqFindings" },
  { from: 1, to: 1, key: "sysGuideSeqVerify" },
  { from: 1, to: 1, key: "sysGuideSeqRoute" },
  { from: 1, to: 3, key: "sysGuideSeqQueue" },
  { from: 3, to: 1, key: "sysGuideSeqDecide" },
  { from: 1, to: 1, key: "sysGuideSeqAudit" },
];

const LANE_W = 186;
const LANE_PAD = 16;
const HEADER_H = 40;
const FIRST_Y = HEADER_H + 34;
const CROSS_H = 48;
const SELF_H = 62;
const SELF_OUT = 34;
const SELF_DROP = 26;

const laneX = (index: number) => LANE_PAD + LANE_W / 2 + index * LANE_W;
const CANVAS_W = LANE_PAD * 2 + LANE_W * ACTORS.length;

export function SystemGuideSequence() {
  const { t } = useI18n();

  let cursor = FIRST_Y;
  const laid = MESSAGES.map((message) => {
    const self = message.from === message.to;
    const item = { ...message, y: cursor, self };
    cursor += self ? SELF_H : CROSS_H;
    return item;
  });
  const canvasH = cursor + 16;

  return (
    <div className="overflow-x-auto">
      <svg
        width={CANVAS_W}
        height={canvasH}
        className="mx-auto"
        role="img"
        aria-label={t("sysGuideSequenceTitle")}
      >
        <defs>
          <marker
            id="sys-guide-arrow"
            viewBox="0 0 8 8"
            refX="7"
            refY="4"
            markerWidth="7"
            markerHeight="7"
            orient="auto-start-reverse"
          >
            <path d="M 0 1 L 7 4 L 0 7 z" fill="#64748b" />
          </marker>
        </defs>

        {ACTORS.map((actorKey, index) => {
          const x = laneX(index);
          const engine = index === 1;
          return (
            <g key={actorKey}>
              <rect
                x={x - LANE_W / 2 + 10}
                y={0}
                width={LANE_W - 20}
                height={HEADER_H}
                rx={8}
                fill={engine ? "#eef2ff" : "#ffffff"}
                stroke={engine ? "#c7d2fe" : "#e2e8f0"}
              />
              <text
                x={x}
                y={HEADER_H / 2 + 4}
                textAnchor="middle"
                fontSize={12}
                fontWeight={700}
                fill={engine ? "#3730a3" : "#334155"}
              >
                {t(actorKey)}
              </text>
              <line
                x1={x}
                y1={HEADER_H}
                x2={x}
                y2={canvasH}
                stroke="#cbd5e1"
                strokeWidth={1}
                strokeDasharray="4 4"
              />
            </g>
          );
        })}

        {laid.map((message) => {
          const label = t(message.key);
          if (message.self) {
            const x = laneX(message.from);
            return (
              <g key={`${message.key}-${message.y}`}>
                <path
                  d={`M ${x} ${message.y} h ${SELF_OUT} v ${SELF_DROP} h ${-SELF_OUT}`}
                  fill="none"
                  stroke="#64748b"
                  strokeWidth={1.25}
                  markerEnd="url(#sys-guide-arrow)"
                />
                <text
                  x={x + SELF_OUT + 10}
                  y={message.y + SELF_DROP / 2 + 4}
                  fontSize={11.5}
                  fill="#475569"
                >
                  {label}
                </text>
              </g>
            );
          }

          const x1 = laneX(message.from);
          const x2 = laneX(message.to);
          return (
            <g key={`${message.key}-${message.y}`}>
              <line
                x1={x1}
                y1={message.y}
                x2={x2}
                y2={message.y}
                stroke="#64748b"
                strokeWidth={1.25}
                markerEnd="url(#sys-guide-arrow)"
              />
              <text
                x={(x1 + x2) / 2}
                y={message.y - 8}
                textAnchor="middle"
                fontSize={11.5}
                fill="#475569"
              >
                {label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
